import type { CSSProperties } from 'react'
import { tokens } from '@/theme'

interface Props {
  /**
   * Background the mark sits on.
   *   light → renders the gold+black variant (for white/paper backgrounds)
   *   dark  → renders the gold+white variant (for inkPanel backgrounds)
   */
  variant?: 'light' | 'dark'
  /** Rendered pixel height of the mark. Width scales from the asset's aspect ratio. */
  size?: number
  /** Makes the mark a real button. In the header this is the route home. */
  onClick?: () => void
  /** Accessible name for the interactive form. Ignored when `onClick` is absent. */
  label?: string
  /**
   * Set false for the glyph alone, without the "TIS AI" wordmark — the
   * footer's sign-off, where the name is already carried by the copyright
   * line next to it.
   */
  showWordmark?: boolean
}

/**
 * Orion AI brand mark — stroked "AI" in gold, paired with the
 * Trelleborg wordmark in Libre Franklin Demi. Renders inline SVG only;
 * no PNG fallback (the public/brand assets aren't bundled and the
 * SVG is the brand-of-record for this build).
 *
 * The browser-tab favicon (public/favicon.svg, public/favicon.ico) redraws the
 * same two polygons on a dark tile — change the points here and there together.
 *
 * With `onClick` it renders a `<button>`, not a clickable div: since the
 * nav dropped to the design's three tabs, the mark is the only route home,
 * and a div would have put that route out of reach of the keyboard.
 * Without `onClick` and without the wordmark it is pure decoration and is
 * hidden from assistive tech.
 */
export function TrelleborgMark({
  variant = 'light',
  size = 40,
  onClick,
  label = 'TIS AI — home',
  showWordmark = true,
}: Props) {
  const textColor = variant === 'dark' ? tokens.paper : tokens.ink
  const textSize = size * 0.62

  const layout: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: size * 0.28,
    userSelect: 'none',
    height: size,
  }

  const artwork = (
    <>
      <svg
        width={size * 1.45}
        height={size}
        viewBox="-20 -4 148 102"
        aria-hidden="true"
        focusable="false"
        style={{ display: 'block', flexShrink: 0 }}
      >
        <polygon
          fill="none"
          stroke={tokens.gold}
          strokeWidth={13}
          strokeLinejoin="miter"
          points="36.610,10.740 81.610,88.682 -8.390,88.682"
        />
        <polygon
          fill={tokens.gold}
          points="51.621,-2.260 66.632,-2.260 122.891,95.182 107.880,95.182"
        />
      </svg>
      {showWordmark && (
        <span
          // Named so a cramped surface can drop the word and keep the glyph;
          // the header does exactly that on a phone. The accessible name
          // comes from the button's aria-label, so nothing is lost with it.
          className="orion-wordmark"
          style={{
            fontFamily: tokens.fontDisplay,
            fontSize: textSize,
            fontWeight: tokens.weightDemi,
            letterSpacing: '-0.01em',
            color: textColor,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          TIS AI
        </span>
      )}
    </>
  )

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        style={{
          ...layout,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        {artwork}
      </button>
    )
  }

  return (
    <div style={{ ...layout, cursor: 'default' }} aria-hidden={showWordmark ? undefined : true}>
      {artwork}
    </div>
  )
}
